import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, or, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  Calendar, 
  Check, 
  X, 
  Loader, 
  RefreshCw, 
  Settings,
  AlertCircle,
  Clock,
  Users,
  FolderOpen,
  RotateCcw,
  CheckCircle,
  Info,
  LogIn,
  LogOut,
  Shield,
  Database,
  Wifi,
  WifiOff
} from 'lucide-react';

// Google Calendar Sync Component with Real-Time Updates
const GoogleCalendarSync = ({ currentUser }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRealTimeSyncActive, setIsRealTimeSyncActive] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [debugData, setDebugData] = useState(null);
  
  // Refs for cleanup
  const unsubscribersRef = useRef([]);
  const syncTimeoutRef = useRef(null);
  const eventMappingRef = useRef(new Map()); // Map Firebase IDs to Google Calendar Event IDs
  
  const [syncConfig, setSyncConfig] = useState({
    syncTasks: true,
    syncRoutines: true,
    syncProjects: true,
    autoSync: true, // הפעלת סנכרון אוטומטי כברירת מחדל
    createReminders: true,
    reminderMinutes: 30,
    calendarId: 'primary',
    realTimeSync: true // סנכרון בזמן אמת
  });

  const [syncStats, setSyncStats] = useState({
    tasksSync: 0,
    routinesSync: 0,
    projectsSync: 0,
    totalEvents: 0,
    lastAutoSync: null
  });

  // Google Calendar API Configuration
  const CLIENT_ID = '1062768182558-q1vjudm07dd2g0guio445pb9d3fve82l.apps.googleusercontent.com';
  const API_KEY = 'AIzaSyDERHB9AYOINDM_Qkt2wlw-OlRoDykY7BY';
  const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest';
  const SCOPES = 'https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/calendar';

  // Initialize Google API
  useEffect(() => {
    const initializeGoogleAPI = () => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);

      const script2 = document.createElement('script');
      script2.src = 'https://apis.google.com/js/api.js';
      script2.async = true;
      script2.defer = true;
      script2.onload = () => {
        window.gapi.load('client', initializeGapiClient);
      };
      document.body.appendChild(script2);
    };

    initializeGoogleAPI();
  }, []);

  const initializeGapiClient = async () => {
    try {
      await window.gapi.client.init({
        apiKey: API_KEY,
        discoveryDocs: [DISCOVERY_DOC],
      });
      console.log('✅ Google API Client initialized');
    } catch (error) {
      console.error('❌ Error initializing Google API:', error);
      setSyncStatus({ type: 'error', message: 'שגיאה באתחול Google API' });
    }
  };

  // Handle Google OAuth2 Sign In
  const handleGoogleSignIn = (silent = false) => {
    if (!window.google?.accounts?.oauth2) {
      if (!silent) {
        setSyncStatus({ type: 'error', message: 'Google API לא נטען. נסה לרענן את העמוד.' });
      }
      return;
    }

    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      prompt: silent ? '' : 'select_account', // אם silent, אל תבקש בחירת חשבון
      callback: (response) => {
        if (response.access_token) {
          setAccessToken(response.access_token);
          setIsConnected(true);
          window.gapi.client.setToken({ access_token: response.access_token });
          
          if (!silent) {
            setSyncStatus({ type: 'success', message: 'התחברת בהצלחה ל-Google Calendar! 🎉' });
          }
          
          // Save token to localStorage with timestamp
          localStorage.setItem('google_calendar_token', response.access_token);
          localStorage.setItem('google_calendar_token_time', Date.now().toString());
          
          // Auto sync after connection
          if (!silent) {
            setTimeout(() => handleSync(response.access_token), 1000);
          }
          
          // Start real-time sync if enabled
          if (syncConfig.realTimeSync) {
            startRealTimeSync();
          }
          
          // Set up token refresh (Google tokens expire after 1 hour)
          setTimeout(() => {
            console.log('🔄 Refreshing Google Calendar token...');
            handleGoogleSignIn(true); // Silent refresh
          }, 50 * 60 * 1000); // Refresh after 50 minutes
        }
      },
      error_callback: (error) => {
        console.error('OAuth Error:', error);
        if (!silent) {
          setSyncStatus({ type: 'error', message: 'שגיאה בהתחברות ל-Google. וודא שהוספת את האימייל שלך ל-Test Users.' });
        }
      }
    });

    tokenClient.requestAccessToken();
  };

  // Handle Sign Out
  const handleGoogleSignOut = () => {
    // Stop real-time sync
    stopRealTimeSync();
    
    if (accessToken) {
      window.google.accounts.oauth2.revoke(accessToken, () => {
        setAccessToken(null);
        setIsConnected(false);
        setIsRealTimeSyncActive(false);
        localStorage.removeItem('google_calendar_token');
        setSyncStatus({ type: 'info', message: 'התנתקת מ-Google Calendar' });
        eventMappingRef.current.clear();
      });
    }
  };

  // Check for saved token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem('google_calendar_token');
    if (savedToken) {
      setAccessToken(savedToken);
      setIsConnected(true);
      if (window.gapi && window.gapi.client) {
        window.gapi.client.setToken({ access_token: savedToken });
      }
      // Load event mapping from localStorage
      const savedMapping = localStorage.getItem('event_mapping');
      if (savedMapping) {
        eventMappingRef.current = new Map(JSON.parse(savedMapping));
      }
    }
  }, []);

  // Save event mapping to localStorage whenever it changes
  const saveEventMapping = () => {
    localStorage.setItem('event_mapping', JSON.stringify(Array.from(eventMappingRef.current.entries())));
  };

  // Helper Functions
  const getPriorityLabel = (priority) => {
    const labels = {
      'important_urgent': 'חשוב ודחוף',
      'important_not_urgent': 'חשוב לא דחוף',
      'not_important_urgent': 'לא חשוב דחוף',
      'not_important_not_urgent': 'לא חשוב לא דחוף',
      'medium': 'בינוני',
      'high': 'גבוה',
      'low': 'נמוך'
    };
    return labels[priority] || 'רגיל';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'important_urgent': '11', // Red
      'important_not_urgent': '5', // Yellow
      'not_important_urgent': '6', // Orange
      'not_important_not_urgent': '8', // Gray
      'high': '11',
      'medium': '5',
      'low': '8'
    };
    return colors[priority] || '1';
  };

  const getFrequencyLabel = (frequency) => {
    const labels = {
      'daily': 'יומית',
      'weekly': 'שבועית',
      'monthly': 'חודשית'
    };
    return labels[frequency] || frequency;
  };

  const getNextWeekday = (targetDay) => {
    const date = new Date();
    const currentDay = date.getDay();
    const distance = (targetDay - currentDay + 7) % 7 || 7;
    date.setDate(date.getDate() + distance);
    return date;
  };

  // Get existing events from Google Calendar
  const getExistingCalendarEvents = async () => {
    try {
      const response = await window.gapi.client.calendar.events.list({
        calendarId: syncConfig.calendarId,
        q: 'taskss',
        maxResults: 1000,
        singleEvents: true,
        orderBy: 'startTime'
      });
      return response.result.items || [];
    } catch (error) {
      console.error('Error fetching existing events:', error);
      return [];
    }
  };

  // Create or Update Calendar Event
  const createOrUpdateCalendarEvent = async (event, taskssId, existingEventId = null) => {
    try {
      // Add unique identifier
      event.extendedProperties = {
        private: {
          taskssId: taskssId,
          source: 'taskss',
          lastUpdated: new Date().toISOString()
        }
      };

      let response;
      if (existingEventId) {
        // Update existing event
        response = await window.gapi.client.calendar.events.update({
          calendarId: syncConfig.calendarId,
          eventId: existingEventId,
          resource: event
        });
        console.log(`✅ Updated event: ${event.summary}`);
      } else {
        // Create new event
        response = await window.gapi.client.calendar.events.insert({
          calendarId: syncConfig.calendarId,
          resource: event
        });
        console.log(`✅ Created event: ${event.summary}`);
      }

      // Store mapping
      if (response.result?.id) {
        eventMappingRef.current.set(taskssId, response.result.id);
        saveEventMapping();
      }

      return response.result;
    } catch (error) {
      console.error('Error creating/updating event:', error);
      return null;
    }
  };

  // Delete Calendar Event
  const deleteCalendarEvent = async (taskssId) => {
    try {
      const eventId = eventMappingRef.current.get(taskssId);
      if (!eventId) {
        console.log(`No calendar event found for ${taskssId}`);
        return false;
      }

      await window.gapi.client.calendar.events.delete({
        calendarId: syncConfig.calendarId,
        eventId: eventId
      });

      // Remove from mapping
      eventMappingRef.current.delete(taskssId);
      saveEventMapping();
      
      console.log(`✅ Deleted event for ${taskssId}`);
      return true;
    } catch (error) {
      console.error('Error deleting event:', error);
      return false;
    }
  };

  // Create event object for task
  const createTaskEvent = (task) => {
    if (!task.dueDate) return null;

    return {
      summary: `📋 ${task.title || 'משימה ללא שם'}`,
      description: `${task.description || ''}\n\n🏷️ עדיפות: ${getPriorityLabel(task.priority)}\n📊 סטטוס: ${task.status}\n⏱️ זמן משוער: ${task.estimate || 1} שעות`,
      start: {
        date: task.dueDate,
        timeZone: 'Asia/Jerusalem'
      },
      end: {
        date: task.dueDate,
        timeZone: 'Asia/Jerusalem'
      },
      colorId: getPriorityColor(task.priority),
      reminders: syncConfig.createReminders ? {
        useDefault: false,
        overrides: [
          { method: 'popup', minutes: syncConfig.reminderMinutes },
          { method: 'email', minutes: syncConfig.reminderMinutes * 2 }
        ]
      } : { useDefault: true }
    };
  };

  // Create event object for routine
  const createRoutineEvent = (routine) => {
    if (!routine.isActive) return null;

    const event = {
      summary: `🔄 ${routine.name || 'שגרה יומית'}`,
      description: `${routine.description || ''}\n\n⏰ שגרה ${getFrequencyLabel(routine.frequency)}\n⏱️ זמן משוער: ${routine.estimatedTime || 30} דקות`,
      colorId: '7',
      reminders: routine.enableNotifications ? {
        useDefault: false,
        overrides: [
          { method: 'popup', minutes: 15 },
          { method: 'email', minutes: 30 }
        ]
      } : { useDefault: true }
    };

    // Set recurrence based on frequency
    if (routine.frequency === 'daily' && routine.dueTime) {
      const startDate = new Date();
      const [hours, minutes] = routine.dueTime.split(':');
      startDate.setHours(parseInt(hours), parseInt(minutes), 0);
      
      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + (routine.estimatedTime || 30));

      event.start = {
        dateTime: startDate.toISOString(),
        timeZone: 'Asia/Jerusalem'
      };
      event.end = {
        dateTime: endDate.toISOString(),
        timeZone: 'Asia/Jerusalem'
      };
      event.recurrence = ['RRULE:FREQ=DAILY'];
    } else if (routine.frequency === 'weekly' && routine.weeklyDay !== undefined) {
      const daysMap = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
      event.recurrence = [`RRULE:FREQ=WEEKLY;BYDAY=${daysMap[routine.weeklyDay]}`];
      
      const nextDate = getNextWeekday(routine.weeklyDay);
      const [hours, minutes] = (routine.dueTime || '09:00').split(':');
      nextDate.setHours(parseInt(hours), parseInt(minutes), 0);
      
      const endDate = new Date(nextDate);
      endDate.setMinutes(endDate.getMinutes() + (routine.estimatedTime || 30));
      
      event.start = {
        dateTime: nextDate.toISOString(),
        timeZone: 'Asia/Jerusalem'
      };
      event.end = {
        dateTime: endDate.toISOString(),
        timeZone: 'Asia/Jerusalem'
      };
    }

    return event;
  };

  // Create event object for project
  const createProjectEvent = (project) => {
    if (!project.startDate || !project.endDate) return null;

    return {
      summary: `🎯 פרויקט: ${project.name || 'פרויקט ללא שם'}`,
      description: `${project.description || ''}\n\n📅 ${project.startDate} עד ${project.endDate}`,
      start: {
        date: project.startDate,
        timeZone: 'Asia/Jerusalem'
      },
      end: {
        date: project.endDate,
        timeZone: 'Asia/Jerusalem'
      },
      colorId: '11',
      reminders: {
        useDefault: false,
        overrides: [
          { method: 'popup', minutes: 1440 },
          { method: 'email', minutes: 2880 }
        ]
      }
    };
  };

  // Fetch data from Firebase
  const fetchFirebaseData = async () => {
    try {
      const userId = currentUser?.uid || currentUser?.id;
      
      if (!userId) {
        console.error('❌ No user ID found');
        return { tasks: [], routines: [], projects: [] };
      }

      console.log('📊 Fetching data for user:', userId);

      // 1. Tasks
      const tasksRef = collection(db, 'tasks');
      const tasksQuery = query(
        tasksRef,
        or(
          where('assigneeId', '==', userId),
          where('assigneeIds', 'array-contains', userId),
          where('creatorId', '==', userId)
        )
      );
      
      const tasksSnapshot = await getDocs(tasksQuery);
      const tasks = tasksSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(task => task.status !== 'הושלם');

      console.log(`✅ Found ${tasks.length} tasks`);

      // 2. Daily Routines
      const routinesRef = collection(db, 'dailyRoutines');
      const routinesQuery = query(
        routinesRef,
        where('isActive', '==', true)
      );
      
      const routinesSnapshot = await getDocs(routinesQuery);
      const routines = routinesSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(routine => {
          return routine.assigneeId === userId || 
                 (routine.assigneeIds && routine.assigneeIds.includes(userId)) ||
                 routine.createdBy === userId;
        });

      console.log(`✅ Found ${routines.length} routines`);

      // 3. Projects
      const projectsRef = collection(db, 'projects');
      const projectsSnapshot = await getDocs(projectsRef);
      const projects = projectsSnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(project => {
          return project.assigneeId === userId || 
                 (project.teamMembers && project.teamMembers.includes(userId));
        });

      console.log(`✅ Found ${projects.length} projects`);

      setDebugData({ tasks, routines, projects });

      return { tasks, routines, projects };
    } catch (error) {
      console.error('❌ Error fetching Firebase data:', error);
      setSyncStatus({ type: 'error', message: 'שגיאה בטעינת נתונים מ-Firebase' });
      return { tasks: [], routines: [], projects: [] };
    }
  };

  // Main Sync Function
  const handleSync = async (token = accessToken) => {
    if (!token) {
      setSyncStatus({ type: 'error', message: 'אנא התחבר תחילה ל-Google Calendar' });
      return;
    }

    setIsSyncing(true);
    setSyncStatus({ type: 'info', message: 'טוען נתונים מ-Firebase... ⏳' });

    try {
      // Fetch data from Firebase
      const { tasks, routines, projects } = await fetchFirebaseData();
      
      setSyncStatus({ type: 'info', message: `נמצאו: ${tasks.length} משימות, ${routines.length} שגרות, ${projects.length} פרויקטים. מסנכרן...` });
      
      // Get existing events from Google Calendar
      const existingEvents = await getExistingCalendarEvents();
      const existingEventIds = new Map();
      existingEvents.forEach(event => {
        const taskssId = event.extendedProperties?.private?.taskssId;
        if (taskssId) {
          existingEventIds.set(taskssId, event.id);
          eventMappingRef.current.set(taskssId, event.id);
        }
      });
      
      let syncedTasks = 0;
      let syncedRoutines = 0;
      let syncedProjects = 0;
      let updatedItems = 0;
      const errors = [];

      // Sync Tasks
      if (syncConfig.syncTasks) {
        for (const task of tasks) {
          const event = createTaskEvent(task);
          if (!event) continue;

          const taskId = `task_${task.id}`;
          const existingEventId = existingEventIds.get(taskId);
          
          const result = await createOrUpdateCalendarEvent(event, taskId, existingEventId);
          if (result) {
            existingEventId ? updatedItems++ : syncedTasks++;
          } else {
            errors.push(`Task: ${task.title}`);
          }
        }
      }

      // Sync Routines
      if (syncConfig.syncRoutines) {
        for (const routine of routines) {
          const event = createRoutineEvent(routine);
          if (!event) continue;

          const routineId = `routine_${routine.id}`;
          const existingEventId = existingEventIds.get(routineId);
          
          const result = await createOrUpdateCalendarEvent(event, routineId, existingEventId);
          if (result) {
            existingEventId ? updatedItems++ : syncedRoutines++;
          } else {
            errors.push(`Routine: ${routine.name}`);
          }
        }
      }

      // Sync Projects
      if (syncConfig.syncProjects) {
        for (const project of projects) {
          const event = createProjectEvent(project);
          if (!event) continue;

          const projectId = `project_${project.id}`;
          const existingEventId = existingEventIds.get(projectId);
          
          const result = await createOrUpdateCalendarEvent(event, projectId, existingEventId);
          if (result) {
            existingEventId ? updatedItems++ : syncedProjects++;
          } else {
            errors.push(`Project: ${project.name}`);
          }
        }
      }

      // Update sync stats
      setSyncStats({
        tasksSync: syncedTasks,
        routinesSync: syncedRoutines,
        projectsSync: syncedProjects,
        totalEvents: syncedTasks + syncedRoutines + syncedProjects,
        lastAutoSync: new Date()
      });

      setLastSyncTime(new Date());
      
      const totalSynced = syncedTasks + syncedRoutines + syncedProjects;
      
      if (errors.length > 0) {
        setSyncStatus({ 
          type: 'warning', 
          message: `סנכרון הושלם חלקית. ${totalSynced} חדשים, ${updatedItems} עודכנו, ${errors.length} שגיאות` 
        });
      } else if (totalSynced === 0 && updatedItems === 0) {
        setSyncStatus({ 
          type: 'info', 
          message: 'הכל מסונכרן! לא נמצאו שינויים חדשים.' 
        });
      } else {
        setSyncStatus({ 
          type: 'success', 
          message: `✅ סנכרון הושלם! ${totalSynced} חדשים, ${updatedItems} עודכנו` 
        });
      }
    } catch (error) {
      console.error('Sync error:', error);
      setSyncStatus({ type: 'error', message: `שגיאה בסנכרון: ${error.message}` });
    } finally {
      setIsSyncing(false);
    }
  };

  // Real-Time Sync Functions
  const startRealTimeSync = () => {
    if (!isConnected || !currentUser || isRealTimeSyncActive) return;

    console.log('🔄 Starting real-time sync...');
    setIsRealTimeSyncActive(true);

    const userId = currentUser?.uid || currentUser?.id;
    if (!userId) {
      console.error('No user ID for real-time sync');
      return;
    }

    // Listen to Tasks changes
    if (syncConfig.syncTasks) {
      const tasksRef = collection(db, 'tasks');
      const tasksQuery = query(
        tasksRef,
        or(
          where('assigneeId', '==', userId),
          where('assigneeIds', 'array-contains', userId),
          where('creatorId', '==', userId)
        )
      );

      const unsubscribeTasks = onSnapshot(tasksQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          const task = { id: change.doc.id, ...change.doc.data() };
          const taskId = `task_${task.id}`;

          // Debounce the sync to avoid too many API calls
          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }

          syncTimeoutRef.current = setTimeout(async () => {
            if (change.type === 'added' || change.type === 'modified') {
              if (task.status !== 'הושלם') {
                const event = createTaskEvent(task);
                if (event) {
                  const existingEventId = eventMappingRef.current.get(taskId);
                  await createOrUpdateCalendarEvent(event, taskId, existingEventId);
                  console.log(`📝 Real-time sync: ${change.type} task ${task.title}`);
                }
              } else {
                // Task completed - remove from calendar
                await deleteCalendarEvent(taskId);
                console.log(`🗑️ Real-time sync: removed completed task ${task.title}`);
              }
            } else if (change.type === 'removed') {
              await deleteCalendarEvent(taskId);
              console.log(`🗑️ Real-time sync: removed task ${task.title}`);
            }
          }, 2000); // Wait 2 seconds before syncing to batch changes
        });
      });

      unsubscribersRef.current.push(unsubscribeTasks);
    }

    // Listen to Routines changes
    if (syncConfig.syncRoutines) {
      const routinesRef = collection(db, 'dailyRoutines');
      const routinesQuery = query(routinesRef, where('isActive', '==', true));

      const unsubscribeRoutines = onSnapshot(routinesQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          const routine = { id: change.doc.id, ...change.doc.data() };
          
          // Check if routine is assigned to current user
          if (routine.assigneeId !== userId && 
              (!routine.assigneeIds || !routine.assigneeIds.includes(userId)) &&
              routine.createdBy !== userId) {
            return;
          }

          const routineId = `routine_${routine.id}`;

          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }

          syncTimeoutRef.current = setTimeout(async () => {
            if (change.type === 'added' || change.type === 'modified') {
              const event = createRoutineEvent(routine);
              if (event) {
                const existingEventId = eventMappingRef.current.get(routineId);
                await createOrUpdateCalendarEvent(event, routineId, existingEventId);
                console.log(`🔄 Real-time sync: ${change.type} routine ${routine.name}`);
              }
            } else if (change.type === 'removed') {
              await deleteCalendarEvent(routineId);
              console.log(`🗑️ Real-time sync: removed routine ${routine.name}`);
            }
          }, 2000);
        });
      });

      unsubscribersRef.current.push(unsubscribeRoutines);
    }

    // Listen to Projects changes
    if (syncConfig.syncProjects) {
      const projectsRef = collection(db, 'projects');
      const unsubscribeProjects = onSnapshot(projectsRef, (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          const project = { id: change.doc.id, ...change.doc.data() };
          
          // Check if project is assigned to current user
          if (project.assigneeId !== userId && 
              (!project.teamMembers || !project.teamMembers.includes(userId))) {
            return;
          }

          const projectId = `project_${project.id}`;

          if (syncTimeoutRef.current) {
            clearTimeout(syncTimeoutRef.current);
          }

          syncTimeoutRef.current = setTimeout(async () => {
            if (change.type === 'added' || change.type === 'modified') {
              const event = createProjectEvent(project);
              if (event) {
                const existingEventId = eventMappingRef.current.get(projectId);
                await createOrUpdateCalendarEvent(event, projectId, existingEventId);
                console.log(`🎯 Real-time sync: ${change.type} project ${project.name}`);
              }
            } else if (change.type === 'removed') {
              await deleteCalendarEvent(projectId);
              console.log(`🗑️ Real-time sync: removed project ${project.name}`);
            }
          }, 2000);
        });
      });

      unsubscribersRef.current.push(unsubscribeProjects);
    }

    setSyncStatus({ type: 'success', message: '✅ סנכרון אוטומטי פעיל' });
  };

  const stopRealTimeSync = () => {
    console.log('⏹️ Stopping real-time sync...');
    
    // Unsubscribe from all listeners
    unsubscribersRef.current.forEach(unsubscribe => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    });
    unsubscribersRef.current = [];
    
    // Clear any pending sync timeouts
    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }
    
    setIsRealTimeSyncActive(false);
    setSyncStatus({ type: 'info', message: 'סנכרון אוטומטי הופסק' });
  };

  // Toggle real-time sync
  const toggleRealTimeSync = () => {
    if (isRealTimeSyncActive) {
      stopRealTimeSync();
      setSyncConfig(prev => ({ ...prev, realTimeSync: false }));
    } else {
      startRealTimeSync();
      setSyncConfig(prev => ({ ...prev, realTimeSync: true }));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRealTimeSync();
    };
  }, []);

  // Start/stop real-time sync based on config and connection
  useEffect(() => {
    if (isConnected && syncConfig.realTimeSync && currentUser && !isRealTimeSyncActive) {
      startRealTimeSync();
    } else if (!syncConfig.realTimeSync && isRealTimeSyncActive) {
      stopRealTimeSync();
    }
  }, [isConnected, syncConfig.realTimeSync, currentUser]);

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-blue-100 rounded-lg">
            <Calendar className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">סנכרון Google Calendar</h2>
            <p className="text-sm text-gray-600 flex items-center gap-2">
              {isConnected ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <span>מחובר</span>
                  {isRealTimeSyncActive && (
                    <>
                      <Wifi className="h-4 w-4 text-blue-600 animate-pulse" />
                      <span className="text-blue-600">סנכרון אוטומטי פעיל</span>
                    </>
                  )}
                </>
              ) : (
                <>
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <span>לא מחובר</span>
                </>
              )}
            </p>
          </div>
        </div>
        
        <div className="flex gap-2">
          <button
            onClick={() => setShowDebug(!showDebug)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Debug"
          >
            <Database className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="הוראות התקנה"
          >
            <Info className="h-5 w-5 text-gray-600" />
          </button>
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="הגדרות"
          >
            <Settings className="h-5 w-5 text-gray-600" />
          </button>
        </div>
      </div>


  {showInstructions && (
    <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
      <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
        <Shield className="h-5 w-5" />
        בדיקת הגדרות
      </h3>
      <div className="space-y-2 text-sm text-blue-800">
        <div className="flex items-center gap-2">
          {CLIENT_ID ? '✅' : '❌'} Client ID מוגדר
        </div>
        <div className="flex items-center gap-2">
          {window.gapi?.client ? '✅' : '❌'} Google API Client נטען
        </div>
        <div className="flex items-center gap-2">
          {currentUser ? '✅' : '❌'} משתמש מחובר למערכת
        </div>
        <div className="flex items-center gap-2">
          {isConnected ? '✅' : '❌'} מחובר ל-Google Calendar
        </div>
        <div className="flex items-center gap-2">
          {isRealTimeSyncActive ? '✅' : '❌'} סנכרון אוטומטי פעיל
        </div>
      </div>
      <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs">
        <strong>חשוב:</strong> וודא שהוספת את האימייל שלך ל-Test Users ב-Google Cloud Console
      </div>
      <div className="mt-2 p-2 bg-green-100 border border-green-300 rounded text-xs">
        <strong>OAuth Mode:</strong> המערכת משתמשת רק ב-OAuth, ללא API Key
      </div>
    </div>
  )}

      {/* Connection Status */}
      <div className="mb-6">
        {!isConnected ? (
          <button
            onClick={handleGoogleSignIn}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-3 shadow-md hover:shadow-lg"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#ffffff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#ffffff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#ffffff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#ffffff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            התחבר עם Google
          </button>
        ) : (
          <div className="space-y-4">
            {/* Connected Status */}
            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-800">מחובר ל-Google Calendar</span>
              </div>
              <button
                onClick={handleGoogleSignOut}
                className="text-sm text-red-600 hover:text-red-700 font-medium flex items-center gap-1"
              >
                <LogOut className="h-4 w-4" />
                התנתק
              </button>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-2 gap-3">
              {/* Manual Sync Button */}
              <button
                onClick={() => handleSync()}
                disabled={isSyncing}
                className={`py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-md
                  ${isSyncing 
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                    : 'bg-green-600 text-white hover:bg-green-700 hover:shadow-lg'
                  }`}
              >
                {isSyncing ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    מסנכרן...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-5 w-5" />
                    סנכרון ידני
                  </>
                )}
              </button>

              {/* Real-time Sync Toggle */}
              <button
                onClick={toggleRealTimeSync}
                className={`py-3 px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-2 shadow-md
                  ${isRealTimeSyncActive 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } hover:shadow-lg`}
              >
                {isRealTimeSyncActive ? (
                  <>
                    <Wifi className="h-5 w-5" />
                    סנכרון אוטומטי פעיל
                  </>
                ) : (
                  <>
                    <WifiOff className="h-5 w-5" />
                    הפעל סנכרון אוטומטי
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
          <h3 className="font-semibold text-gray-700 mb-3">הגדרות סנכרון</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Sync Tasks */}
            <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:border-blue-300">
              <input
                type="checkbox"
                checked={syncConfig.syncTasks}
                onChange={(e) => setSyncConfig({...syncConfig, syncTasks: e.target.checked})}
                className="w-4 h-4 text-blue-600"
              />
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">סנכרן משימות</span>
              </div>
            </label>

            {/* Sync Routines */}
            <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:border-blue-300">
              <input
                type="checkbox"
                checked={syncConfig.syncRoutines}
                onChange={(e) => setSyncConfig({...syncConfig, syncRoutines: e.target.checked})}
                className="w-4 h-4 text-blue-600"
              />
              <div className="flex items-center gap-2">
                <RotateCcw className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">סנכרן שגרות</span>
              </div>
            </label>

            {/* Sync Projects */}
            <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:border-blue-300">
              <input
                type="checkbox"
                checked={syncConfig.syncProjects}
                onChange={(e) => setSyncConfig({...syncConfig, syncProjects: e.target.checked})}
                className="w-4 h-4 text-blue-600"
              />
              <div className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">סנכרן פרויקטים</span>
              </div>
            </label>

            {/* Create Reminders */}
            <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer hover:border-blue-300">
              <input
                type="checkbox"
                checked={syncConfig.createReminders}
                onChange={(e) => setSyncConfig({...syncConfig, createReminders: e.target.checked})}
                className="w-4 h-4 text-blue-600"
              />
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium">צור תזכורות</span>
              </div>
            </label>
          </div>

          {/* Reminder Time */}
          {syncConfig.createReminders && (
            <div className="mt-3">
              <label className="text-sm font-medium text-gray-700">
                זמן תזכורת (דקות לפני האירוע):
              </label>
              <input
                type="number"
                value={syncConfig.reminderMinutes}
                onChange={(e) => setSyncConfig({...syncConfig, reminderMinutes: parseInt(e.target.value)})}
                className="mt-1 w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                min="5"
                max="1440"
              />
            </div>
          )}

          {/* Real-time Sync Info */}
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Wifi className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">סנכרון אוטומטי</p>
                <p>כאשר מופעל, כל שינוי במשימות, שגרות או פרויקטים יתעדכן אוטומטית ביומן Google תוך מספר שניות.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sync Status Messages */}
      {syncStatus && (
        <div className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${
          syncStatus.type === 'success' ? 'bg-green-50 border border-green-200' :
          syncStatus.type === 'error' ? 'bg-red-50 border border-red-200' :
          syncStatus.type === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
          'bg-blue-50 border border-blue-200'
        }`}>
          {syncStatus.type === 'success' ? <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" /> :
           syncStatus.type === 'error' ? <X className="h-5 w-5 text-red-600 mt-0.5" /> :
           syncStatus.type === 'warning' ? <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" /> :
           <Info className="h-5 w-5 text-blue-600 mt-0.5" />}
          <span className={`text-sm font-medium ${
            syncStatus.type === 'success' ? 'text-green-800' :
            syncStatus.type === 'error' ? 'text-red-800' :
            syncStatus.type === 'warning' ? 'text-yellow-800' :
            'text-blue-800'
          }`}>
            {syncStatus.message}
          </span>
        </div>
      )}

      {/* Sync Statistics */}
      {syncStats.totalEvents > 0 && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <CheckCircle className="h-5 w-5 text-blue-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-blue-900">{syncStats.tasksSync}</div>
            <div className="text-xs text-blue-700">משימות</div>
          </div>
          
          <div className="bg-green-50 p-3 rounded-lg text-center">
            <RotateCcw className="h-5 w-5 text-green-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-green-900">{syncStats.routinesSync}</div>
            <div className="text-xs text-green-700">שגרות</div>
          </div>
          
          <div className="bg-purple-50 p-3 rounded-lg text-center">
            <FolderOpen className="h-5 w-5 text-purple-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-purple-900">{syncStats.projectsSync}</div>
            <div className="text-xs text-purple-700">פרויקטים</div>
          </div>
          
          <div className="bg-gray-100 p-3 rounded-lg text-center">
            <Calendar className="h-5 w-5 text-gray-600 mx-auto mb-1" />
            <div className="text-2xl font-bold text-gray-900">{syncStats.totalEvents}</div>
            <div className="text-xs text-gray-700">סה"כ</div>
          </div>
        </div>
      )}

      {/* Last Sync Time */}
      {lastSyncTime && (
        <div className="mt-4 text-center text-sm text-gray-500">
          <div>סנכרון ידני אחרון: {lastSyncTime.toLocaleTimeString('he-IL')} {lastSyncTime.toLocaleDateString('he-IL')}</div>
          {syncStats.lastAutoSync && (
            <div>סנכרון אוטומטי אחרון: {syncStats.lastAutoSync.toLocaleTimeString('he-IL')}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarSync;